import type { AsyncDuckDB, DuckDBConfig, DuckDBBundle, Logger, LogLevel, DuckDBDataProtocol } from '@duckdb/duckdb-wasm';
import { Engine, EngineFeatures, TableRef, RegisterTableParams, TableSourceType, TableRegistrationOptions, TableChangeCallback, ExportToURLOptions } from '../core/engine';
import { MaterializedTableData } from '../core/table-data';
import { TableView, BaseTableView, ViewSource } from '../core/table-view';
import { Query, TableSchema, MaterializeOptions, TableFormat, ColumnSchema, TableDataSource } from '../core/types';
import { DuckDBWasmSQLBuilder } from './sql-builder';
import { DataType, Float64, Int64, Utf8, Bool, DateDay, TimestampMillisecond } from 'apache-arrow';

/**
 * Configuration for DuckDB engine
 */
export type DuckDBEngineConfig = DuckDBConfig;

/**
 * Information about a registered table
 */
interface RegisteredTable {
  schema: TableSchema;
  path: string;
  source: TableDataSource;
}

/**
 * DuckDB WASM module interface
 */
interface DuckDBModule {
  LogLevel: typeof LogLevel;
  DuckDBDataProtocol: typeof DuckDBDataProtocol;
  AsyncDuckDB: typeof AsyncDuckDB;
  ConsoleLogger: new (logLevel: LogLevel) => Logger;
  getJsDelivrBundles: () => any;
  selectBundle: (bundles: any) => Promise<DuckDBBundle>;
  createWorker: (worker: string) => Promise<Worker>;
}

// Cache the bundle promise to avoid multiple fetches
let __bundle: Promise<DuckDBBundle> | undefined = undefined;
let __duckdb: DuckDBModule | undefined = undefined;

/**
 * Get the DuckDB module
 */
async function getDuckDB(): Promise<DuckDBModule> {
  if (__duckdb) {
    return __duckdb;
  }
  
  const duckdbModule = await import('@duckdb/duckdb-wasm');
  __duckdb = duckdbModule as unknown as DuckDBModule;
  return __duckdb;
}

/**
 * Get the DuckDB bundle
 */
async function getBundle() {
  if (__bundle) {
    return __bundle;
  }
  
  const duckdb = await getDuckDB();
  const JSDELIVR_BUNDLES = duckdb.getJsDelivrBundles();
  __bundle = Promise.resolve(duckdb.selectBundle(JSDELIVR_BUNDLES));
  
  return __bundle;
}

/**
 * Core function to register a file with DuckDB and create a view
 * This is a pure function that handles the core registration logic
 */
async function registerFileInDuckDB(params: {
  file: File,
  db: AsyncDuckDB,
  options?: TableRegistrationOptions & {
    upsert?: boolean // Whether to replace existing table/view if it exists
  }
}): Promise<TableRef> {
  const { file, db, options } = params;

  // Create a clean name while preserving the original case and format
  const cleanFileName = file.name
    // Remove file extension (everything after the last dot)
    .replace(/\.[^/.]+$/, '')
    // Replace spaces and special characters with single underscore
    .replace(/[ ()-]+/g, '_')
    // Remove any remaining non-alphanumeric characters except underscores
    .replace(/[^a-zA-Z0-9_]/g, '');

  // Create valid DuckDB identifiers
  const registeredFileName = `file_${cleanFileName}`; // Internal file registration name
  const viewName = cleanFileName; // Use the same name for view and TableRef
  
  // Determine the file format
  const extension = file.name.split('.').pop()?.toLowerCase();
  let format: TableFormat;
  
  switch (extension) {
    case 'json':
      format = TableFormat.JSON;
      break;
    case 'parquet':
      format = TableFormat.PARQUET;
      break;
    case 'arrow':
      format = TableFormat.ARROW;
      break;
    case 'tsv':
      format = TableFormat.TSV;
      break;
    default:
      format = TableFormat.CSV;
  }

  // Register the file with DuckDB using the registered file name
  const duckdb = await getDuckDB();
  await db.registerFileHandle(registeredFileName, file, duckdb.DuckDBDataProtocol.BROWSER_FILEREADER, false);
  
  // Create a connection to execute SQL
  const conn = await db.connect();
  
  try {
    // Check if object (table or view) already exists
    const existingObjects = await conn.query(`
      SELECT table_name, table_type
      FROM information_schema.tables 
      WHERE table_schema = 'main' 
      AND table_name = '${viewName}'
    `);

    // If object exists and upsert is not enabled, throw error
    if (existingObjects.toArray().length > 0 && !options?.upsert) {
      throw new Error(`Table/view "${viewName}" already exists. Use upsert option to replace.`);
    }

    // Drop existing object if upsert is enabled
    if (existingObjects.toArray().length > 0 && options?.upsert) {
      // Drop both table and view to ensure clean slate
      await conn.query(`DROP TABLE IF EXISTS "${viewName}"`);
      await conn.query(`DROP VIEW IF EXISTS "${viewName}"`);
    }

    // Create view SQL based on format - use distinct names for file and view
    let viewSql: string;
    switch (format) {
      case TableFormat.PARQUET:
        viewSql = `CREATE OR REPLACE VIEW "${viewName}" AS SELECT * FROM parquet_scan('${registeredFileName}')`;
        break;
      case TableFormat.JSON:
        viewSql = `CREATE OR REPLACE VIEW "${viewName}" AS SELECT * FROM read_json('${registeredFileName}', auto_detect=true)`;
        break;
      case TableFormat.ARROW:
        viewSql = `CREATE OR REPLACE VIEW "${viewName}" AS SELECT * FROM arrow_scan('${registeredFileName}')`;
        break;
      case TableFormat.TSV:
        viewSql = `CREATE OR REPLACE VIEW "${viewName}" AS SELECT * FROM read_csv('${registeredFileName}', delim='\t', header=true, auto_detect=true)`;
        break;
      default:
        viewSql = `CREATE OR REPLACE VIEW "${viewName}" AS SELECT * FROM read_csv_auto('${registeredFileName}', sample_size=100, all_varchar=false, header=true, auto_type_candidates=['INTEGER', 'BIGINT', 'DOUBLE', 'TIMESTAMP', 'DATE', 'TIME', 'BOOLEAN', 'VARCHAR'])`;
    }
    
    await conn.query(viewSql);
    
    // Query the view to get the schema
    const result = await conn.query(`SELECT * FROM "${viewName}" LIMIT 10`);
    
    // Create schema from result
    const schema: TableSchema = {
      columns: result.schema.fields.map(field => {
        const duckDBType = field.type.toString();
        const mappedType = mapDuckDBTypeToColumnType(duckDBType);
        return {
          name: field.name,
          type: mappedType,
          nullable: true
        };
      })
    };

    // Create and return TableRef directly
    return {
      id: file.name,
      path: viewName, // Keep using viewName for path since it needs to be SQL-safe
      schema,
      source: {
        type: TableSourceType.LOCAL_FILE,
        fileId: registeredFileName,
        format,
        schema
      }
    };
  } finally {
    await conn.close();
  }
}

// Helper function to map DuckDB types to Apache Arrow DataType objects
function mapDuckDBTypeToColumnType(duckDBType: string): DataType {
  const type = duckDBType.toUpperCase();
  
  if (type.includes('VARCHAR') || type.includes('TEXT')) {
    return new Utf8();
  } else if (type.includes('INTEGER') || type.includes('BIGINT') || type.includes('INT32') || type.includes('INT64')) {
    return new Int64();
  } else if (type.includes('DOUBLE') || type.includes('FLOAT') || type.includes('DECIMAL') || type.includes('NUMERIC')) {
    return new Float64();
  } else if (type.includes('BOOLEAN')) {
    return new Bool();
  } else if (type.includes('DATE')) {
    return new DateDay();
  } else if (type.includes('TIMESTAMP')) {
    return new TimestampMillisecond();
  } else if (type.includes('TIME')) {
    return new TimestampMillisecond(); // Using TimestampMillisecond for TIME as well since Arrow doesn't have a dedicated TIME type in WASM
  }

  return new Utf8();
}

/**
 * DuckDB WebAssembly engine implementation
 * 
 * This class provides a factory method for creating initialized instances.
 * 
 * @memoryUsage This engine maintains significant memory structures:
 * - DuckDB WASM instance has its own memory pool
 * - Maintains internal buffers and caches
 * - Memory usage increases with:
 *   - Number of registered tables
 *   - Size of query results
 *   - Concurrent query execution
 * - Always call dispose() when done to free resources
 */
export class DuckDBEngine implements Engine {
  private db: AsyncDuckDB | null = null;
  private isInitialized = false;
  private readonly tables = new Map<string, RegisteredTable>();
  
  /**
   * Callbacks to be called when tables change
   * @private
   */
  private tableChangeCallbacks: Set<TableChangeCallback> = new Set();

  /**
   * Create a new DuckDB engine instance
   * @private This constructor is private - use DuckDBEngine.create() instead
   */
  private constructor() {
    // fileStore initialization removed
  }

  /**
   * Create and initialize a new DuckDBEngine instance
   * @returns A promise that resolves to an initialized DuckDBEngine instance
   */
  public static async create(): Promise<DuckDBEngine> {
    const engine = new DuckDBEngine();
    await engine.initialize();
    return engine;
  }

  get features(): EngineFeatures {
    return {
      supportsSQL: true
    };
  }

  /**
   * Initialize the DuckDB instance
   * @returns Promise that resolves when initialization is complete
   */
  public async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // In Node.js environment, we need to handle initialization differently
      if (typeof window === 'undefined') {
        // For testing, we'll just mark as initialized without actual DuckDB setup
        this.isInitialized = true;
        this.db = {
          // Mock methods used in tests
          connect: async () => ({
            query: async () => {},
            close: async () => {}
          }),
          registerFileBuffer: async () => {},
          instantiate: async () => {}
        } as unknown as AsyncDuckDB;
        return;
      }

      // Get the bundle
      const bundle = await getBundle();
      
      // Create the worker
      if (!bundle.mainWorker) {
        throw new Error('Bundle main worker is null or undefined');
      }
      
      try {
        const duckdb = await getDuckDB();
        // Use the worker URL directly instead of creating a Blob
        const worker = await duckdb.createWorker(bundle.mainWorker);
        
        // Create DuckDB instance with console logger
        const logger = new duckdb.ConsoleLogger(duckdb.LogLevel.WARNING);
        const db = new duckdb.AsyncDuckDB(logger, worker);
        
        // Instantiate DuckDB with the bundle
        await db.instantiate(bundle.mainModule, bundle.pthreadWorker);
        
        // Open the database
        await db.open({
          path: ":memory:", 
          query: {
            castBigIntToDouble: true,
            castDecimalToDouble: true,
            castTimestampToDate: true,
            castDurationToTime64: true
          }
        });

        this.db = db;
        this.isInitialized = true;
      } catch (error) {
        console.error('Error during DuckDB initialization:', error);
        this.db = null;
        throw new Error(`Failed to initialize DuckDB: ${error}`);
      }
    } catch (error) {
      console.error('Error during DuckDB initialization:', error);
      this.db = null;
      throw new Error(`Failed to initialize DuckDB: ${error}`);
    }
  }

  /**
   * Ensures the engine is initialized before performing operations
   * @private
   */
  private async ensureInitialized(): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }
  }

  /**
   * Infer schema from a URL data source
   * @param params The URL details to infer schema from
   * @returns Promise that resolves to the inferred schema
   */
  private async inferSchemaFromURL(params: { url: string }): Promise<TableSchema> {
    const { url } = params;
    
    if (!this.isInitialized || !this.db) {
      throw new Error('DuckDB not initialized');
    }
    
    const conn = await this.db.connect();
    try {
      // Run a simple query directly against the URL to get sample data
      const query = `SELECT * FROM '${url}' LIMIT 10`;
      
      // Execute the query to get an Arrow table
      const result = await conn.query(query);
      
      // Extract schema information from the Arrow table
      const columns: ColumnSchema[] = result.schema.fields.map(field => {
        const dataType = field.type;
        
        return {
          name: field.name,
          type: dataType,
          nullable: field.nullable
        };
      });
      
      if (columns.length === 0) {
        throw new Error('Could not infer schema: no columns detected');
      }
      
      return { columns };
    } finally {
      conn.close();
    }
  }

  async registerTable(params: RegisterTableParams): Promise<TableRef> {
    await this.ensureInitialized();
    
    const { source, options = {} } = params;
    
    // Check if we need to infer schema for URL sources
    let inferredSchema: TableSchema | undefined;
    if (source.type === TableSourceType.URL && 
        (!source.schema || source.schema.columns.length === 0)) {
      try {
        inferredSchema = await this.inferSchemaFromURL({ url: source.url });
        // Add the inferred schema to the source
        source.schema = inferredSchema;
      } catch (error) {
        throw new Error(`Failed to infer schema from URL: ${error}`);
      }
    }
    
    // Get schema from the appropriate source
    const schema = this.getSchemaFromSource(source);
    
    if (options.validateSchema) {
      await this.validateTableSchema(schema);
    }
    
    // Generate or use provided path
    const id = options.path || this.generateTableId(source);
    
    // Check if a table with this ID already exists
    if (this.tables.has(id)) {
      throw new Error(`Table already exists: ${id}`);
    }
    
    // Store only the schema and path information
    const path = options.path || this.getDefaultPath(source, id);
    this.tables.set(id, {
      schema,
      path,
      source
    });
    
    // Create a table reference
    const tableRef: TableRef = {
      id,
      path,
      schema,
      source
    };
    
    // Register the table with DuckDB
    if (this.db) {
      const conn = await this.db.connect();
      try {
        await this.registerTableWithDuckDB(conn, id, source);
      } finally {
        conn.close();
      }
    }
    
    // Notify callbacks that tables have changed
    this.notifyTableChange();
    
    return tableRef;
  }

  private async registerTableWithDuckDB(conn: { query: (sql: string) => Promise<any>; insertArrowTable?: (table: any, options: any) => Promise<any> }, id: string, source: TableDataSource): Promise<void> {
    switch (source.type) {
      case TableSourceType.MATERIALIZED:
        if (!conn.insertArrowTable) {
          throw new Error('DuckDB connection does not support insertArrowTable method');
        }
        await conn.insertArrowTable(source.data.table, {
          name: id
        });
        break;

      case TableSourceType.LOCAL_FILE:
        // Get the file content directly from the source
        if (!source.file) {
          throw new Error(`File not provided for fileId: ${source.fileId}`);
        }
        
        // Read the file data
        const buffer = await source.file.arrayBuffer();
        
        // Register the file data directly with DuckDB
        await this.db!.registerFileBuffer(
          source.fileId,
          new Uint8Array(buffer)
        );
        
        // Create view from the registered file based on format for lazy loading
        switch (source.format) {
          case TableFormat.PARQUET:
            await conn.query(`CREATE VIEW "${id}" AS SELECT * FROM parquet_scan('${source.fileId}')`);
            break;
          case TableFormat.CSV:
            await conn.query(`CREATE VIEW "${id}" AS SELECT * FROM read_csv_auto('${source.fileId}')`);
            break;
          case TableFormat.TSV:
            // For TSV files, specify the delimiter as tab
            await conn.query(`CREATE VIEW "${id}" AS SELECT * FROM read_csv('${source.fileId}', delim='\t', auto_detect=true)`);
            break;
          case TableFormat.JSON:
            await conn.query(`CREATE VIEW "${id}" AS SELECT * FROM read_json_auto('${source.fileId}')`);
            break;
          default:
            throw new Error(`Unsupported format for local file: ${source.format}`);
        }
        break;

      case TableSourceType.URL:
        // For URLs, we can use DuckDB's HTTP functionality
        const format = source.format;
        const materialized = (source as { materialized?: boolean }).materialized ?? false;
        const sql = (() => {
          if (materialized) {
            // Create a materialized table instead of a view
            if (format === TableFormat.PARQUET) {
              return `CREATE TABLE "${id}" AS SELECT * FROM parquet_scan('${source.url}')`;
            } else if (format === TableFormat.CSV) {
              return `CREATE TABLE "${id}" AS SELECT * FROM read_csv_auto('${source.url}')`;
            } else if (format === TableFormat.TSV) {
              return `CREATE TABLE "${id}" AS SELECT * FROM read_csv('${source.url}', delim='\t', auto_detect=true)`;
            } else if (format === TableFormat.JSON) {
              return `CREATE TABLE "${id}" AS SELECT * FROM read_json_auto('${source.url}')`;
            }
          } else {
            // Create a view as before
            if (format === TableFormat.PARQUET) {
              return `CREATE VIEW "${id}" AS SELECT * FROM parquet_scan('${source.url}')`;
            } else if (format === TableFormat.CSV) {
              return `CREATE VIEW "${id}" AS SELECT * FROM read_csv_auto('${source.url}')`;
            } else if (format === TableFormat.TSV) {
              return `CREATE VIEW "${id}" AS SELECT * FROM read_csv('${source.url}', delim='\t', auto_detect=true)`;
            } else if (format === TableFormat.JSON) {
              return `CREATE VIEW "${id}" AS SELECT * FROM read_json_auto('${source.url}')`;
            }
          }
          throw new Error(`Unsupported format for URL source: ${format}`);
        })();
        await conn.query(sql);
        break;
    }
  }

  async getTableView(ref: TableRef): Promise<TableView> {
    if (!this.isInitialized || !this.db) {
      throw new Error('DuckDB not initialized');
    }

    const tableInfo = this.tables.get(ref.id);
    if (!tableInfo) {
      throw new Error(`Table not found: ${ref.id}`);
    }

    return new BaseTableView(ref.schema, { type: 'table', tableRef: ref });
  }

  async transform(view: TableView, query: Query): Promise<TableView> {
    if (!this.isInitialized || !this.db) {
      throw new Error('DuckDB not initialized');
    }

    return new BaseTableView(view.schema, {
      type: 'query',
      tableRef: view.source.tableRef,
      query
    });
  }

  async unregisterTable(ref: TableRef): Promise<void> {
    await this.ensureInitialized();
    
    // Check if the table exists
    if (!this.tables.has(ref.id)) {
      throw new Error(`Table not found: ${ref.id}`);
    }
    
    // Get the table information
    const table = this.tables.get(ref.id)!;
    
    // Remove the table from DuckDB
    if (this.db) {
      const conn = await this.db.connect();
      try {
        // Determine if this is a table or view based on the source type
        const isView = table.source.type === TableSourceType.LOCAL_FILE || 
                       table.source.type === TableSourceType.URL;
        
        if (isView) {
          // For LOCAL_FILE and URL sources, we create views
          await conn.query(`DROP VIEW IF EXISTS "${ref.path}"`);
        } else {
          // For MATERIALIZED sources, we create tables
          await conn.query(`DROP TABLE IF EXISTS "${ref.path}"`);
        }
      } catch (error) {
        console.error(`Error dropping table/view ${ref.path}:`, error);
      } finally {
        conn.close();
      }
    }
    
    // Remove the table from the registry
    this.tables.delete(ref.id);
    
    // Notify callbacks that tables have changed
    this.notifyTableChange();
  }

  async materialize(view: TableView, options?: MaterializeOptions): Promise<MaterializedTableData> {
    if (!this.isInitialized || !this.db) {
      throw new Error('DuckDB not initialized');
    }

    const conn = await this.db.connect();
    try {
      const { source } = view;
      let sql: string;

      switch (source.type) {
        case 'table':
          sql = `SELECT * FROM "${source.tableRef.path}"`;
          break;
        case 'query':
          sql = DuckDBWasmSQLBuilder.buildQuery({
            tableName: source.tableRef.path,
            query: source.query
          });
          break;
      }

      // Remove trailing semicolons before adding LIMIT
      sql = sql.trim().replace(/;+$/, '');

      // Apply paging with default limit of 1000 rows, but only if the SQL doesn't already have a LIMIT clause
      const { size = 1000, number = 0 } = options?.page || {};
      const offset = size * number;
      
      // Check if the SQL already has a LIMIT clause
      if (!sql.toUpperCase().includes(' LIMIT ')) {
        sql += ` LIMIT ${size} OFFSET ${offset}`;
      } else if (!sql.toUpperCase().includes(' OFFSET ')) {
        // If there's already a LIMIT but no OFFSET, add just the OFFSET
        sql += ` OFFSET ${offset}`;
      }

      const result = await conn.query(sql);
      return new MaterializedTableData(result);
    } finally {
      await conn.close();
    }
  }

  /**
   * Export query results to a URL without pagination
   * @param options Export options including view and signedUrl
   * @returns Promise that resolves when the export is complete
   */
  async exportToURL(options: ExportToURLOptions): Promise<void> {
    if (!this.isInitialized || !this.db) {
      throw new Error('DuckDB not initialized');
    }

    const { view, signedUrl } = options;
    
    // Validate inputs
    if (!view) {
      throw new Error('View is required for export');
    }
    
    if (!signedUrl) {
      throw new Error('Signed URL is required for export');
    }
    
    // Validate URL format (basic check)
    try {
      new URL(signedUrl);
    } catch (error) {
      throw new Error(`Invalid signed URL: ${signedUrl}`);
    }
    
    const conn = await this.db.connect();
    
    try {
      const source: ViewSource = view.source;
      let sql: string;

      // Build the SQL query without pagination limits
      if (source.type === 'table') {
        sql = `SELECT * FROM "${source.tableRef.path}"`;
      } else if (source.type === 'query') {
        sql = DuckDBWasmSQLBuilder.buildQuery({
          tableName: source.tableRef.path,
          query: source.query
        });
      } else {
        // This should never happen with proper ViewSource types
        throw new Error('Unsupported source type');
      }

      // Remove trailing semicolons
      sql = sql.trim().replace(/;+$/, '');

      // Create a temporary file name for the Parquet export
      const tempFileName = `export_${Date.now()}.parquet`;
      
      // Use DuckDB's COPY statement to export to a Parquet file
      await conn.query(`COPY (${sql}) TO '${tempFileName}' (FORMAT PARQUET)`);
      
      // Get the Parquet data from the temporary file
      const parquetBuffer = await this.db!.copyFileToBuffer(tempFileName);
      
      if (!parquetBuffer || parquetBuffer.byteLength === 0) {
        throw new Error('Failed to export data to Parquet format');
      }
      
      // Upload the Parquet data to the signed URL
      const uploadResponse = await fetch(signedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/octet-stream',
        },
        body: parquetBuffer,
      });
      
      if (!uploadResponse.ok) {
        throw new Error(`Failed to upload data: ${uploadResponse.status} ${uploadResponse.statusText}`);
      }
      
      // Clean up the temporary file
      try {
        await this.db!.dropFile(tempFileName);
      } catch (error) {
        console.warn(`Failed to delete temporary file ${tempFileName}:`, error);
      }
    } catch (error) {
      console.error('Export failed:', error);
      throw new Error(`Failed to export data: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      await conn.close();
    }
  }

  async dispose(): Promise<void> {
    // Close the database connection
    if (this.db) {
      try {
        // The correct method is terminate(), not close()
        await this.db.terminate();
      } catch (error) {
        console.error('Error closing DuckDB:', error);
      }
      this.db = null;
    }
    
    this.isInitialized = false;
    this.tables.clear();
  }

  private getSchemaFromSource(source: TableDataSource): TableSchema {
    switch (source.type) {
      case TableSourceType.LOCAL_FILE:
        return source.schema;
      case TableSourceType.URL:
        if (!source.schema) {
          throw new Error('Schema is required for URL source. Use an engine implementation that supports schema inference.');
        }
        return source.schema;
      case TableSourceType.MATERIALIZED:
        return source.data.schema;
    }
  }

  private generateTableId(source: TableDataSource): string {
    const uuid = crypto.randomUUID();
    
    switch (source.type) {
      case TableSourceType.LOCAL_FILE:
        return `local-${uuid}`;
      case TableSourceType.URL:
        return `url-${uuid}`;
      default:
        return uuid;
    }
  }

  private getDefaultPath(source: TableDataSource, id: string): string {
    switch (source.type) {
      case TableSourceType.LOCAL_FILE:
        // For files, keep the extension and ensure path starts with /
        const fileName = source.fileId;
        return fileName.startsWith('/') ? fileName : `/${fileName}`;
      
      case TableSourceType.URL:
        try {
          const url = new URL(source.url);
          // Extract path from URL and ensure it starts with /
          // For URLs, keep the extension if it's a file path
          const pathParts = url.pathname.split('/').filter(Boolean);
          return pathParts.length > 0 ? `/${pathParts.join('/')}` : `/url_${id}`;
        } catch {
          // If URL parsing fails, create a fallback path without extension
          return `/url_${id}`;
        }
      
      case TableSourceType.MATERIALIZED:
        // For materialized tables, create a path without extension
        return `/table_${id}`;
    }
  }

  private async validateTableSchema(schema: TableSchema): Promise<void> {
    if (!schema || !schema.columns || schema.columns.length === 0) {
      throw new Error('Invalid table schema');
    }
  }

  /**
   * List all registered tables
   * @returns Array of TableRef objects for all registered tables
   */
  async listRegisteredTables(): Promise<TableRef[]> {
    if (!this.isInitialized) {
      await this.ensureInitialized();
    }

    const result: TableRef[] = [];
    
    // Convert the internal tables map to TableRef objects
    for (const [id, tableInfo] of this.tables.entries()) {
      const tableRef: TableRef = {
        id,
        path: tableInfo.path,
        schema: tableInfo.schema,
        source: tableInfo.source  // Use the stored source instead of creating a placeholder
      };
      
      result.push(tableRef);
    }
    
    return result;
  }

  /**
   * Notify all registered callbacks that tables have changed
   * @private
   */
  private notifyTableChange(): void {
    // Call all registered callbacks
    this.tableChangeCallbacks.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('Error in table change callback:', error);
      }
    });
  }
  
  /**
   * Register a callback to be called when tables change
   * @param callback The callback to register
   * @returns A function to unregister the callback
   */
  onTablesChange(callback: TableChangeCallback): () => void {
    this.tableChangeCallbacks.add(callback);
    
    // Return a function to unregister the callback
    return () => {
      this.tableChangeCallbacks.delete(callback);
    };
  }

  /**
   * Register a table from a URL
   * This is a convenience method for loading data from a URL
   * @param params Configuration object with URL registration parameters
   * @param params.url The URL to load data from
   * @param params.format The format of the data (CSV, JSON, etc.)
   * @param params.options Optional registration options
   * @returns Table reference
   */
  async registerFromURL(params: { 
    url: string; 
    format: TableFormat; 
    options?: TableRegistrationOptions;
    materialized?: boolean 
  }): Promise<TableRef> {
    await this.ensureInitialized();
    
    const { url, format, options, materialized = false } = params;
    
    // Let the registerTable method handle schema inference
    return this.registerTable({
      source: {
        type: TableSourceType.URL,
        url,
        format,
        schema: { columns: [] }, // Empty schema that will be replaced by inference
        materialized // Pass through the materialized flag
      } as const,
      options
    });
  }

  /**
   * Register a table from a File object
   * This method uses DuckDB's direct file handling capabilities
   * @param file File object to register
   * @param options Optional registration options
   * @returns Table reference
   */
  async registerFromFile(file: File, options?: TableRegistrationOptions): Promise<TableRef> {
    await this.ensureInitialized();
    
    if (!this.db) {
      throw new Error('DuckDB not initialized');
    }
    
    try {
      // Clean the file name to match how it will be registered in DuckDB
      const cleanFileName = file.name
        .replace(/\.[^/.]+$/, '') // Remove file extension
        .replace(/[ ()-]+/g, '_') // Replace spaces and special characters with underscore
        .replace(/[^a-zA-Z0-9_]/g, ''); // Remove any remaining non-alphanumeric characters
      
      // Check if a table with this name already exists in our registry
      const existingTableId = Array.from(this.tables.keys()).find(id => {
        const tableInfo = this.tables.get(id);
        return tableInfo && tableInfo.path === cleanFileName;
      });
      
      // If we found an existing table with the same path and upsert is enabled, unregister it first
      if (existingTableId && options?.upsert) {
        const existingTable = { id: existingTableId, path: cleanFileName } as TableRef;
        await this.unregisterTable(existingTable);
      }
      
      // Register the file with DuckDB
      const tableRef = await registerFileInDuckDB({
        file,
        db: this.db,
        options
      });
      
      // Store the table information
      this.tables.set(tableRef.id, {
        schema: tableRef.schema,
        path: tableRef.path,
        source: tableRef.source
      });
      
      // Notify callbacks that tables have changed
      this.notifyTableChange();
      
      return tableRef;
    } catch (error) {
      console.error(`DuckDBEngine: Error registering file ${file.name}:`, error);
      throw new Error(`Failed to register file: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
} 